import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    Phone,
    Video,
    MessageSquareText,
    Layers,
    StepBack,
} from "lucide-react";
import { useSelector } from "react-redux";
import axios from "axios";
import { ICON_MAP } from "../../utilities/iconcatalog.tsx";
import ModernFilePicker from "./support_admin/modernfilepicker.tsx";
import ContactSalesModal from "../../pages/ContactSalesModal.tsx";
import { Button } from "./button.jsx";
import SalesBookingModal from "../../pages/SalesBookingModal.tsx";

const QuickIcon = ({ name, className = "w-5 h-5" }) => {
    if (name === "chat") return <MessageSquareText className={className} />;
    if (name === "call") return <Phone className={className} />;
    return <Video className={className} />;
};

type KAM = {
    id: string;
    name: string;
    country: string; // ISO-2
    available: boolean;
    recommended?: boolean;
    phone?: string; // E.164, e.g. +47...
    headshotSrc?: string;
};

function routeKAMsByCountry(kams: KAM[], userCountry: string): KAM[] {
    // Simple geo routing: exact country match first, else return all.
    const exact = kams.filter((k) => k.country === userCountry);
    return exact.length ? exact : kams;
}

function sortKAMs(kams: KAM[]): KAM[] {
    // Recommended first, then available, then name.
    return [...kams].sort((a, b) => {
        const ar = a.recommended ? 1 : 0;
        const br = b.recommended ? 1 : 0;
        if (ar !== br) return br - ar;

        const aa = a.available ? 1 : 0;
        const ba = b.available ? 1 : 0;
        if (aa !== ba) return ba - aa;

        return a.name.localeCompare(b.name);
    });
}

export function TeletorgetHero({
    ctas = [
        { label: "Book a demo", href: "#", kind: "primary" },
        { label: "Kontakt salg", href: "#", kind: "secondary" },
    ],
    onRegisterClick,
    baseUrl,
    handleChipClick,
    from,
    setHeroData,
    heroData,
}) {
    const objectUrlsRef = React.useRef([]);
    const isCMS = from === "support-client";
    const client = useSelector((state) => state.client.client);
    const default_configs = client ? JSON.parse(client.default_configs) : {};
    const footer_settings = client ? JSON.parse(client.footer_settings) : {};

    const hero_settings = client ? JSON.parse(client.hero_settings) : {};
    const theme_settings = client ? JSON.parse(client.theme_settings) : {};

    const [chipsLoading, setChipsLoading] = useState(false);

    const DEFAULT_HERO_IMAGES = [
        "../../hero.png",
        "../../hero-court.jpg",
        "../../hero-global.png",
    ];
    const heroImages =
        hero_settings?.selectedHero?.[theme_settings?.selectedHeroKey]?.images;
    const images = isCMS
        ? heroData?.images?.length
            ? heroData.images
            : DEFAULT_HERO_IMAGES
        : heroImages?.length
            ? heroImages
            : DEFAULT_HERO_IMAGES;

    const removeImage = (index) => {
        setHeroData(prev => {
            const updated = prev.images.filter((_, i) => i !== index);

            return {
                ...prev,
                images: updated.length ? updated : undefined,
                imageFiles: prev.imageFiles?.filter((_, i) => i !== index),
            };
        });

        setCurrentSlide(0);
    };

    const quick = [
        { label: "Chat", href: "#chat", icon: "chat" },
        { label: "Ring", href: "#", icon: "call" },
        { label: "Video", href: "#agents", icon: "video" },
    ];

    const [chips, setChips] = useState([]);
    const [subChips, setSubChips] = useState([]);
    const [subChipsLoaded, setSubChipsLoaded] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    /* Fetch chips */
    useEffect(() => {
        if (!client?.uid) return;

        axios
            .get(baseUrl + "/getCategories", { params: { client_uid: client.uid, from } })
            .then((response) => {
                if (response.data.status === "OK") {
                    setChips(response.data.data);
                }
            });
    }, [client]);

    /* Auto-slide */
    useEffect(() => {
        if (!images.length) return;
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % images.length);
        }, 10000);
        return () => clearInterval(interval);
    }, [images.length]);

    useEffect(() => {
        if (currentSlide >= images.length) {
            setCurrentSlide(0);
        }
    }, [images, currentSlide]);

    const onRingClick = () => {
        if (default_configs.WebRTC !== "yes") {
            window.location.href = "tel:" + footer_settings.footer_phone;
        } else {
            document.getElementById("agents")?.scrollIntoView({ behavior: "smooth" });
        }
    };

    const displayTitle = isCMS
        ? hero_settings?.selectedHero?.[theme_settings?.selectedHeroKey]?.title
            ? hero_settings?.selectedHero?.[theme_settings?.selectedHeroKey]?.title
            : heroData?.title || "Title Here"
        : hero_settings?.selectedHero?.[theme_settings?.selectedHeroKey]?.title
            ? hero_settings?.selectedHero?.[theme_settings?.selectedHeroKey]?.title
            : heroData?.title || "Title Here";

    const displayBody = isCMS
        ? hero_settings?.selectedHero?.[theme_settings?.selectedHeroKey]?.body
            ? hero_settings?.selectedHero?.[theme_settings?.selectedHeroKey]?.body
            : heroData?.body || "Body Here"
        : hero_settings?.selectedHero?.[theme_settings?.selectedHeroKey]?.body
            ? hero_settings?.selectedHero?.[theme_settings?.selectedHeroKey]?.body
            : heroData?.body || "Body Here";

    const [titleEditing, setTitleEditing] = useState(false);
    const [bodyEditing, setBodyEditing] = useState(false);

    const [titleInput, setTitleInput] = useState("");
    const [bodyInput, setBodyInput] = useState("");
    useEffect(() => {
        setHeroData(prev => ({
            ...prev,
            title: displayTitle,
            body: displayBody,
        }))
    }, [])
    const startTitleEdit = () => {
        if (!isCMS) return;
        setTitleInput(displayTitle);
        setTitleEditing(true);
    };

    const startBodyEdit = () => {
        if (!isCMS) return;
        setBodyInput(displayBody);
        setBodyEditing(true);
    };

    const saveTitle = () => {
        setHeroData(prev => ({
            ...prev,
            title: titleInput,
        }));
        setTitleEditing(false);
    };

    const saveBody = () => {
        setHeroData(prev => ({
            ...prev,
            body: bodyInput,
        }));
        setBodyEditing(false);
    };

    const [imageFiles, setImageFiles] = useState([]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                setTitleEditing(false);
                setBodyEditing(false);
            }
            if (e.key === "Enter") {
                if (titleEditing) saveTitle();
                if (bodyEditing) saveBody();
            }
        };

        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [titleEditing, bodyEditing, titleInput, bodyInput]);

    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const previews = files.map(file => URL.createObjectURL(file));

        setHeroData(prev => ({
            ...prev,
            images: previews,
            imageFiles: files, // keep originals for upload
        }));
    };

    const handleCategoryClick = async (category) => {
        try {
            setChipsLoading(true);
            setSubChipsLoaded(true);
            await axios
                .get(baseUrl + "/getSkills", { params: { client_uid: client.uid, skill_cate_id: category.id } })
                .then((response) => {
                    if (response.data.status === "OK") {
                        setSubChips(response.data.data);
                    }
                });
        } catch (e) {
            console.error(e);
        } finally {
            setChipsLoading(false);
        }
    }

    useEffect(() => {
        if (!isCMS) return;
        if (!heroImages?.length) return;

        setHeroData(prev => {
            // Already normalized → do nothing
            if (prev?.images?.length) return prev;

            return {
                ...prev,
                images: heroImages,
                imageFiles: [], // API images don't have File objects
            };
        });
    }, [isCMS, heroImages]);

    useEffect(() => {
        return () => {
            objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
            objectUrlsRef.current = [];
        };
    }, []);

    const closeAllModals = () => {
        setOpenDemo(false);
        setOpenSales(false);
        setShowKAM(false);
    };
    const [openDemo, setOpenDemo] = useState(false);
    const [openSales, setOpenSales] = useState(false);
    const [showKAM, setShowKAM] = useState(false);

    // Close modals on ESC (priority: KAM -> Success -> Demo -> Sales)
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== "Escape") return;
            if (showKAM) return void setShowKAM(false);
            if (openDemo) return void setOpenDemo(false);
            if (openSales) return void setOpenSales(false);
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [showKAM, openDemo, openSales]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                closeAllModals();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    // Mock – replace with geo/IP later
    const userCountry = "NO";

    // Mock data – replace with API later
    const kams: KAM[] = useMemo(
        () => [
            {
                id: "kam_anna",
                name: "Anna Berg",
                country: "NO",
                available: true,
                recommended: true,
                phone: "+4700000000",
                headshotSrc: "/stock/headshot-anna.jpg",
            },
            {
                id: "kam_thomas",
                name: "Thomas Johansen",
                country: "NO",
                available: false,
                phone: "+4700000001",
                headshotSrc: "/stock/headshot-thomas.jpg",
            },
            {
                id: "kam_lars",
                name: "Lars Nilsen",
                country: "SE",
                available: true,
                phone: "+4600000000",
                headshotSrc: "/stock/headshot-lars.jpg",
            },
        ],
        []
    );

    const routedKams = useMemo(
        () => sortKAMs(routeKAMsByCountry(kams, userCountry)),
        [kams, userCountry]
    );

    return (
        <div className="w-full max-w-full mx-auto">
            <div className="relative w-full overflow-hidden shadow-xl h-screen">

                {/* === BACKGROUND CAROUSEL === */}
                <AnimatePresence>
                    <motion.img
                        key={currentSlide}
                        src={images[currentSlide]}
                        alt="Hero background"
                        initial={{ opacity: 0, scale: 1, filter: "blur(6px)" }}
                        animate={{ opacity: 1, scale: 1.12, filter: "blur(0px)" }}
                        exit={{
                            opacity: 0,
                            transition: { duration: 0.6 }
                        }}
                        transition={{
                            opacity: { duration: 0.8, ease: "easeOut" },
                            scale: {
                                duration: 10, // 👈 slow cinematic zoom
                                ease: "easeOut",
                            },
                        }}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                </AnimatePresence>

                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />

                {/* === CONTENT === */}
                <div className="relative z-10 flex items-center justify-start h-full py-8 md:py-0">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-white max-w-lg sm:max-w-xl md:max-w-2xl px-4 sm:px-6 md:px-12"
                    >
                        {from === "support-client" && (
                            <ModernFilePicker
                                label=""
                                accept="image/png, image/jpeg"
                                multiple
                                onFile={(files) => {
                                    if (!Array.isArray(files) || !files.length) return;

                                    // clean old URLs
                                    objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
                                    objectUrlsRef.current = [];

                                    const previews = files.map(file => {
                                        const url = URL.createObjectURL(file);
                                        objectUrlsRef.current.push(url);
                                        return url;
                                    });

                                    setHeroData(prev => ({
                                        ...prev,
                                        images: previews,     // ✅ REPLACE
                                        imageFiles: files,
                                    }));

                                    setCurrentSlide(0);
                                }}
                            />
                            // <input type="file" multiple="multiple" accept="image/png, image/jpeg" onChange={handleImageSelect} className="w-full" />
                        )}
                        {from === "support-client" && heroData?.images?.length > 0 && (
                            <div className="mt-4 flex gap-3 flex-wrap">
                                {heroData.images.map((img, i) => (
                                    <div key={i} className="relative w-24 h-16 rounded-lg overflow-hidden">
                                        <img src={img} className="w-full h-full object-cover" />

                                        <button
                                            onClick={() => removeImage(i)}
                                            className="absolute top-1 right-1 bg-red-600/70 hover:bg-red-600 text-white rounded-full w-6 h-6"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {!titleEditing ? (
                            <h1
                                onClick={startTitleEdit}
                                className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight"
                            >
                                {displayTitle}
                            </h1>
                        ) : (
                            <input
                                value={titleInput}
                                onChange={(e) => setTitleInput(e.target.value)}
                                className="w-full text-black text-3xl font-extrabold"
                                autoFocus
                            />
                        )}

                        {!bodyEditing ? (
                            <p
                                onClick={startBodyEdit}
                                className="mt-4 text-base sm:text-lg md:text-xl text-white/90"
                            >
                                {displayBody}
                            </p>
                        ) : (
                            <textarea
                                value={bodyInput}
                                onChange={(e) => setBodyInput(e.target.value)}
                                className="w-full mt-4 text-black text-base sm:text-lg md:text-xl"
                                autoFocus
                            />
                        )}


                        {/* Chips */}
                        {theme_settings?.skill_settings?.selectedSkill === "skillsA" && (
                            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">

                                {from === "support-client" && [1, 2, 3].map((c, i) => {
                                    let HeaderIcon = null;


                                    const iconDef = c.picture ? ICON_MAP[c.picture] : undefined;
                                    HeaderIcon = iconDef?.Icon || Layers;
                                    return (
                                        <button
                                            key={c.id ?? i}
                                            onClick={() => handleChipClick(c)}
                                            className="flex items-center gap-2 rounded-xl bg-white/95 text-gray-900 px-4 py-3 text-sm hover:bg-white transition"
                                        >
                                            {/* Image option */}

                                            {HeaderIcon && (
                                                <HeaderIcon className="h-4 w-4 text-sky-600" />
                                            )}

                                            <span className="font-medium">Skill {c}</span>
                                        </button>
                                    )
                                })}

                                {from === "home" && chipsLoading && (
                                    <div className="flex items-center justify-center h-64">
                                        <div className="flex flex-col items-center">
                                            <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                                            <p className="mt-3 text-gray-600 animate-pulse">Laster inn subskill...</p>
                                        </div>
                                    </div>
                                )}

                                {from === "home" && subChipsLoaded && !chipsLoading && (
                                    <button
                                        onClick={() => setSubChipsLoaded(false)}
                                        className="flex items-center gap-2 rounded-xl bg-white/95 text-gray-900 px-4 py-3 text-sm hover:bg-white transition"
                                    ><StepBack className="h-8 w-8 text-[var(--primary-color)]" />Back</button>
                                )}

                                {from === "home" && !chipsLoading && !subChipsLoaded && chips.map((c, i) => {
                                    let HeaderIcon = null;


                                    const iconDef = c.skill_category_icon ? ICON_MAP[c.skill_category_icon] : undefined;
                                    HeaderIcon = iconDef?.Icon || Layers;

                                    return (
                                        <button
                                            key={c.id ?? i}
                                            onClick={() => handleCategoryClick(c)}
                                            className="flex items-center gap-2 rounded-xl bg-white/95 text-gray-900 px-4 py-3 text-sm hover:bg-white transition"
                                        >
                                            {/* Image option */}

                                            {HeaderIcon && (
                                                <HeaderIcon className="h-8 w-8 text-[var(--primary-color)]" />
                                            )}

                                            <span className="font-medium">{c.name}</span>
                                        </button>
                                    );
                                })}
                                {from === "home" && !chipsLoading && subChipsLoaded && subChips.map((c, i) => {
                                    let HeaderIcon = null;


                                    const iconDef = c.picture ? ICON_MAP[c.picture] : undefined;
                                    HeaderIcon = iconDef?.Icon || Layers;

                                    return (
                                        <button
                                            key={c.id ?? i}
                                            onClick={() => handleChipClick(c.name)}
                                            className="flex items-center gap-2 rounded-xl bg-white/95 text-gray-900 px-4 py-3 text-sm hover:bg-white transition"
                                        >
                                            {/* Image option */}

                                            {HeaderIcon && (
                                                <HeaderIcon className="h-8 w-8 text-[var(--primary-color)]" />
                                            )}

                                            <span className="font-medium">{c.name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* CTAs */}
                        <div className="mt-6 flex flex-wrap gap-3">
                            {ctas.map((c, i) => (
                                <a
                                    key={i}
                                    href={c.href}
                                    onClick={(e) => {
                                        if (c.kind === "secondary") {
                                            e.preventDefault();
                                            setOpenSales(true)
                                        } else if (c.kind === "primary") {
                                            e.preventDefault();
                                            setOpenDemo(true);
                                        }
                                    }}
                                    style={{
                                        backgroundColor:
                                            from === "support-client"
                                                ? c.kind === "primary"
                                                    ? heroData.primaryColor
                                                    : heroData.secondaryColor
                                                : undefined,
                                    }}
                                    className={`px-5 py-3 rounded-xl font-semibold text-sm transition
                                        ${from === "support-client"
                                            ? c.kind === "primary"
                                                ? "text-white"
                                                : "text-gray-900"
                                            : c.kind === "primary"
                                                ? "bg-[var(--primary-color)] text-white hover:bg-[var(--primary-color)]/10"
                                                : "bg-[var(--secondary-color)] text-gray-900 hover:bg-[var(--primary-color)]/10"
                                        }
                                    `}
                                >
                                    {c.label}
                                </a>
                            ))}
                        </div>

                        {/* Quick actions */}
                        {/* <div className="mt-5 flex flex-wrap gap-3">
                            {quick.map((q, i) => (
                                <a
                                    key={i}
                                    href={q.href}
                                    onClick={(e) => {
                                        if (q.icon === "call") {
                                            e.preventDefault();
                                            onRingClick();
                                        }
                                    }}
                                    className="inline-flex items-center gap-2 rounded-full bg-white/95 text-gray-900 px-4 py-2 text-sm hover:bg-white transition"
                                >
                                    <QuickIcon name={q.icon} />
                                    {q.label}
                                </a>
                            ))}
                        </div> */}
                    </motion.div>
                </div>

                {/* === DOTS === */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                    {images.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentSlide(i)}
                            className={`w-2.5 h-2.5 rounded-full transition ${i === currentSlide ? "bg-white" : "bg-white/40"
                                }`}
                        />
                    ))}
                </div>
            </div>

            {/* Sales booking modal */}
            <SalesBookingModal
                open={openDemo}
                onClose={() => setOpenDemo(false)}
                baseUrl={baseUrl}
                client={client}
            />

            {/* Contact sales modal */}
            <ContactSalesModal
                open={openSales}
                onClose={() => setOpenSales(false)}
                onCallClick={() => {
                    setOpenSales(false);
                    setShowKAM(true);
                }}
                showKAM={showKAM}
                setShowKAM={setShowKAM}
                setOpenSales={setOpenSales}
                client={client}
                baseUrl={baseUrl}
            />
        </div>
    );
}


export function Hero({ hero, onRegisterClick, baseUrl, handleChipClick }) {
    return (
        <div className="min-h-screen bg-neutral-100">
            <TeletorgetHero onRegisterClick={onRegisterClick} baseUrl={baseUrl} handleChipClick={handleChipClick} from={"home"} setHeroData={() => console.log('e')} heroData={null} />
        </div>
    );
}